import {
  CreateFieldValueOnView,
  EditFieldValueOnView,
} from 'presentation/fieldValue/fieldValueOnView';
import {
  ComponentType,
  createContext,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import useFormEdit from 'views/issue/forms/useFormEdit';
import { useCustomizedField } from '../../dataProviders/withCustomizedField';
import { showErrorToastAndLog } from 'services/toaster';
import {
  parseChanges,
  saveFieldValues,
  resolveNewFieldsAfterProcessesUpdate,
} from './model';
import { ALL_PROCESS_CODES } from 'shared/domain/process/types/model';
import { findById } from 'shared/utils/id/findById';

type AddFieldValue = (fieldValue: CreateFieldValueOnView) => void;
type EditFieldValue = (fieldValue: EditFieldValueOnView) => void;

interface FieldValuesFormContext {
  submitForm: () => Promise<void>;
  addFieldValue: AddFieldValue;
  editFieldValue: EditFieldValue;
  isPosting: boolean;
  newFields: CreateFieldValueOnView[];
  fieldChanges: EditFieldValueOnView[];
  disableValue: (id: string) => void;
  enableValue: (id: string) => void;
  hasChanges: boolean;
  formName: 'fieldValues';
}

const WithFieldValuesFormContext = createContext<
  FieldValuesFormContext | undefined
>(undefined);
const WithFieldValuesForm: FC<{
  children?: ReactNode;
  instantSubmit: boolean;
}> = ({ children, instantSubmit }) => {
  const formName = 'fieldValues';
  const { fieldName, useFieldValues } = useCustomizedField();
  const { resync, allItems } = useFieldValues();
  const [newFields, setNewFields] = useState<CreateFieldValueOnView[]>([]);
  const [fieldChanges, setFieldChanges] = useState<EditFieldValueOnView[]>(
    []
  );
  const { setEditing } = useFormEdit();

  // Mirrors `newFields`, but also updated synchronously on every
  // `setFieldValueProcesses` call so back-to-back invocations within the
  // same tick (before React flushes the state update) still see each
  // other's changes instead of racing on a stale closure.
  const newFieldsRef = useRef(newFields);
  newFieldsRef.current = newFields;

  const hasChanges = useMemo(
    () => Boolean(newFields.length || fieldChanges.length),
    [newFields, fieldChanges]
  );
  const submitForm = useCallback(
    async (providedNewFields?: CreateFieldValueOnView[]): Promise<void> => {
      setIsPosting(true);
      try {
        await saveFieldValues(
          fieldName,
          providedNewFields || newFields,
          parseChanges(allItems, fieldChanges)
        );
      } catch (error) {
        showErrorToastAndLog('Error while updating a field value', error);
      } finally {
        setIsPosting(false);
        resync();
        setNewFields([]);
        setFieldChanges([]);
        setEditing(false, formName);
      }
    },
    [newFields, fieldChanges, fieldName, allItems, setEditing, resync]
  );
  const addFieldValue: AddFieldValue = useCallback(
    (fieldValue) => {
      setEditing(true, formName);

      const prev = newFieldsRef.current;
      const index = prev.findIndex((createFieldValue) => {
        return createFieldValue.localId === fieldValue.localId;
      });

      let updatedNewFields: CreateFieldValueOnView[];
      if (index === -1) {
        updatedNewFields = [...prev, fieldValue];
      } else {
        updatedNewFields = [...prev];
        updatedNewFields[index] = fieldValue;
      }

      newFieldsRef.current = updatedNewFields;
      setNewFields(updatedNewFields);

      if (instantSubmit) {
        submitForm([fieldValue]);
      }
    },
    [setEditing, instantSubmit, submitForm]
  );
  const editFieldValue: EditFieldValue = useCallback(
    (fieldValue) => {
      setFieldChanges((prev) => {
        const fieldIndex = prev.findIndex(
          (field) => field._id === fieldValue._id
        );
        fieldIndex < 0
          ? (prev[prev.length] = fieldValue)
          : (prev[fieldIndex] = {
              ...prev[fieldIndex],
              ...fieldValue,
            });

        return [...prev];
      });
      setEditing(true, formName);
    },
    [setEditing]
  );

  const setFieldValueProcesses = useCallback(
    (id: string, processes: string[]) => {
      const updatedNewFields = resolveNewFieldsAfterProcessesUpdate(
        newFieldsRef.current,
        id,
        processes
      );
      if (updatedNewFields) {
        newFieldsRef.current = updatedNewFields;
        setNewFields(updatedNewFields);
        return;
      }

      const existingField = findById(fieldChanges, id);
      editFieldValue(
        existingField
          ? { ...existingField, processes }
          : { _id: id, processes }
      );
    },
    [fieldChanges, editFieldValue]
  );

  const disableValue = useCallback(
    (id: string) => setFieldValueProcesses(id, []),
    [setFieldValueProcesses]
  );

  const enableValue = useCallback(
    (id: string) => setFieldValueProcesses(id, ALL_PROCESS_CODES),
    [setFieldValueProcesses]
  );

  const [isPosting, setIsPosting] = useState(false);

  const ctx: FieldValuesFormContext = useMemo(() => {
    return {
      submitForm,
      isPosting,
      editFieldValue,
      addFieldValue,
      fieldChanges,
      newFields,
      enableValue,
      disableValue,
      hasChanges,
      formName,
    };
  }, [
    submitForm,
    isPosting,
    fieldChanges,
    newFields,
    editFieldValue,
    addFieldValue,
    enableValue,
    disableValue,
    hasChanges,
  ]);

  return (
    <WithFieldValuesFormContext.Provider value={ctx}>
      {children}
    </WithFieldValuesFormContext.Provider>
  );
};

function useFieldValuesForm(): FieldValuesFormContext {
  const context = useContext(WithFieldValuesFormContext);
  if (context === undefined) {
    throw new Error(
      'useFieldValuesForm must be used within an FieldValuesFormContext'
    );
  }
  return context;
}

const withFieldValuesForm =
  (Component: ComponentType<any>, instantSubmit?: boolean) =>
  ({ ...props }): ReactElement => (
    <WithFieldValuesForm instantSubmit={!!instantSubmit}>
      <Component {...props} />
    </WithFieldValuesForm>
  );

export { useFieldValuesForm, withFieldValuesForm, WithFieldValuesForm };
